import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import moment from 'moment';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import classes from './agenda.module.scss';
import Calendar from './Calendar/Calendar';
import api from '../../../api/api';
import VisitView from './VisitView/VisitView';
import { useQuery } from '../../../utils/hooks/useQuery';
import { changeMenuPath } from '../../../store/actions/menu';
import Button from '../../_common/Form/Buttons/Button';
import VisitModal from '../../_common/Modal/VisitModal/VisitModal';
import Loader from '../../_common/Loader/Loader';
import NoteModal from '../../_common/Modal/NoteModal/NoteModal';
import accountHelpers from '../../../utils/accountHelpers';
import { deleteVisitRequest } from '../../../store/actions/physicians';
import AttentionModal from '../../_common/Modal/AttentionModal/AttentionModal';

const sortingList = (visitorsList, sortBy) => {
  let data = visitorsList;

  if (sortBy) {
    data = visitorsList.filter(({ Rep }) => Rep === sortBy);
  }

  const groupByDate = _.groupBy(data, (r) => moment.utc(r.dateTime).local().format('YYYY-MM-DD'));
  const list = _.map(groupByDate, (val, key) => ({
    date: key,
    visits: val,
    repCount: _.uniq(val.map((v) => v.Rep)).length,
  }));

  return _.orderBy(list, (l) => new Date(l.date));
};

const Agenda = () => {
  const { pathname } = useLocation();
  const { query, setQuery, queryToStringify } = useQuery();

  const dispatch = useDispatch();

  const firstLoad = useRef(true);
  const [rep, setRep] = useState('');
  const [date, setDate] = useState(query.date || moment().format('YYYY-MM-DD'));
  const [editInfo, setEditInfo] = useState({});
  const [list, setList] = useState([]);
  const [visitList, setVisitList] = useState([]);
  const [visitListIndex, setVisitListIndex] = useState(0);

  const [visitedUsers, setVisitedUsers] = useState([]);

  const [isScheduleModalOpen, scheduleModalToggle] = useState(false);

  const [isScheduleEdit, scheduleEditToggle] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [noteModalData, setNoteModalData] = useState({});
  const [note, setNote] = useState('');
  const [noteSaveLoading, setNoteSaveLoading] = useState(false);

  const [appointment, setAppointment] = useState(null);

  const [loading, loadingToggle] = useState(true);

  const getPhysicianVisitList = async (month = moment(moment(query.date).isValid() ? query.date : new Date()).format('YYYY-MM'), afterUpdate, rapName) => {
    loadingToggle(!afterUpdate);

    if (!visitList.find((l) => moment(l.date).format('YYYY-MM') === month) || afterUpdate) {
      const { data: { response } } = await api.getPhysicianVisits({
        date: moment(month).toISOString(),
        endDate: moment(month).add(1, 'month').toISOString(),
      });

      const sortVisitList = _.orderBy(
        _.uniqBy([...sortingList(response), ...visitList], 'date'),
        (l) => new Date(l.date),
      );

      const sortList = _.orderBy(_.uniqBy([...response, ...list], 'id'), (l) => new Date(l.dateTime));

      setVisitList(sortVisitList);
      setList(sortList);

      if (query.rep) {
        setCurrentRep(query.rep, sortList, moment(query.date).isValid() ? query.date : null);
      } else {
        setCurrentDate(date || moment().format('YYYY-MM-DD'), sortVisitList, rapName || appointment?.[0]?.Rep);
      }
    }

    loadingToggle(false);
  };

  useEffect(() => {
    (async () => {
      await getPhysicianVisitList();

      firstLoad.current = false;
    })();
  }, []);

  useEffect(() => {
    if (!firstLoad.current) {
      const newQuery = { date, rep };
      dispatch(changeMenuPath('Agenda', `${pathname}${queryToStringify(newQuery)}`));

      setQuery(newQuery);
    }
  }, [date, rep]);

  const setCurrentRep = (currentRep, data = list, currentDate) => {
    setRep(currentRep);

    const sortList = sortingList(data, currentRep);

    setVisitList(sortList);

    if (currentDate) {
      setCurrentDate(currentDate, sortList, currentRep);
    } else {
      const lastIndex = _.findLastIndex(sortList);

      setDate(sortList[lastIndex]?.date || '');
      setVisitListIndex(lastIndex || 0);
      setVisitedUsers(sortList[lastIndex]?.visits || []);

      setAppointment(
        (_.groupBy(sortList[lastIndex]?.visits, 'Rep')?.[currentRep]
          || Object.values(_.groupBy(sortList[lastIndex]?.visits, 'Rep'))[0]),
      );
    }
  };

  const setCurrentDate = (currentDate, data = visitList, currentRep) => {
    setDate(currentDate);

    const findDateIndex = data.findIndex((l) => l.date === currentDate);

    setVisitedUsers(data[findDateIndex]?.visits || []);

    setAppointment(
      (_.groupBy(data[findDateIndex]?.visits, 'Rep')?.[currentRep]
        || Object.values(_.groupBy(data[findDateIndex]?.visits, 'Rep'))[0]),
    );

    if (findDateIndex) setVisitListIndex(findDateIndex);
  };

  const changeVisitDate = (prev) => {
    const findDateIndex = visitList.findIndex((l) => l.date === date);
    const changeToIndex = prev ? findDateIndex - 1 : findDateIndex + 1;

    setVisitedUsers(visitList[changeToIndex]?.visits);
    setVisitListIndex(changeToIndex);
    setAppointment(Object.values(_.groupBy(visitList[changeToIndex]?.visits, 'Rep'))[0]);
    setDate(visitList[changeToIndex]?.date);
  };

  const closeNoteModal = useCallback(() => {
    setNoteModalData({});
    setNote('');
  }, []);

  const changeNote = useCallback((path, value) => {
    setNote(value);
  }, []);

  const setAgendaInfo = useCallback((info) => {
    const agendaDate = moment.utc(info.dateTime).local().format('YYYY-MM-DD');
    const agendaTime = moment.utc(info.dateTime).local().format('hh:mm A');

    setEditInfo({
      ...info,
      date: agendaDate,
      time: agendaTime,
    });

    scheduleEditToggle(true);
  }, []);

  const deleteVisit = useCallback(async () => {
    setDeleteLoading(true);

    try {
      await dispatch(deleteVisitRequest(deletingId));
      const month = moment(date).format('YYYY-MM');
      const { data: { response } } = await api.getPhysicianVisits({
        date: moment(month).toISOString(),
        endDate: moment(month).add(1, 'month').toISOString(),
      });

      const sortVisitList = _.orderBy(
        _.uniqBy([...sortingList(response), ...visitList.filter((v) => !v.date.startsWith(month))], 'date'),
        (l) => new Date(l.date),
      );

      const sortList = list.filter((v) => v.id !== deletingId);

      setVisitList(sortVisitList);
      setList(sortList);

      if (query.rep) {
        setCurrentRep(query.rep, sortList, moment(query.date).isValid() ? query.date : null);
      } else {
        setCurrentDate(date || moment().format('YYYY-MM-DD'), sortVisitList, query.rep || appointment?.[0]?.Rep);
      }
    } catch (e) {
      toast.error('Something went wrong');
      console.warn(e);
    }

    setDeleteLoading(false);

    setDeletingId('');
  }, [deletingId, query, visitList, list]);

  const saveNote = useCallback(async () => {
    if (!note) {
      toast.error('Please fill Note field');
    } else {
      setNoteSaveLoading(true);
      const { visitId, physicianId } = noteModalData;

      const sendData = {
        visitId,
        physicianId,
        userId: accountHelpers.getUser().id,
        name: accountHelpers.getUser().name,
        note, // .replace(/#/g, '%23') Allows literal hashtags to be written in note
        userImage: accountHelpers.getUser().link,
      };

      await api.addPhysicianNote(sendData);

      const currentDate = moment(list.find((l) => l.id === visitId).dateTime).format('YYYY-MM');
      await getPhysicianVisitList(currentDate, true);
      closeNoteModal();
      setNoteSaveLoading(false);
    }
  }, [note, noteModalData, list]);

  const closeVisitModal = () => {
    scheduleEditToggle(false);
    scheduleModalToggle(false);
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('width');
    document.body.ontouchmove = () => true;
  };

  return (
    <div className={classes.agenda_wrapper}>
      {firstLoad.current
        ? <Loader />

        : (
          <>
            <Calendar
              date={date}
              onChange={setCurrentDate}
              visitList={visitList}
              onMonthChange={(d) => getPhysicianVisitList(moment(d).format('YYYY-MM'))}
              currentRep={rep}
              loading={loading}
              setRep={setCurrentRep}
              dateFormat="YYYY-MM-DD"
            />

            {!!visitedUsers.length && (
              <VisitView
                currentDate={date}
                visitedUsers={visitedUsers}
                showPrevBtn={!!visitListIndex}
                showNextBtn={visitListIndex !== visitList.length - 1}
                changeVisitDate={changeVisitDate}
                appointment={appointment}
                setAppointment={setAppointment}
                setAgendaInfo={setAgendaInfo}
                setDeletingId={setDeletingId}
                openingNoteAddModal={setNoteModalData}
              />
            )}

            <Button
              title="Schedule new visit"
              className={classes.schedule_btn}
              addButton
              type="button"
              reverseColor
              onClick={() => scheduleModalToggle(true)}
            />

            {isScheduleModalOpen && (
              <VisitModal
                isOpenModal={isScheduleModalOpen}
                onClose={closeVisitModal}
                currentDate={date}
                requestAfterSave={(rapName) => getPhysicianVisitList(moment(date).format('YYYY-MM'), true, rapName)}
              />
            )}

            {isScheduleEdit && (
              <VisitModal
                isOpenModal={isScheduleEdit}
                onClose={closeVisitModal}
                currentDate={editInfo.date}
                currentTime={editInfo.time}
                physicianName={editInfo.Physician}
                physicianId={editInfo.PhysicianId}
                repName={editInfo.Rep}
                repId={editInfo.RepId}
                groupName={editInfo.group}
                id={editInfo.id}
                requestAfterSave={() => getPhysicianVisitList(moment(editInfo.date).format('YYYY-MM'), true)}
              />
            )}

            <NoteModal
              isOpenModal={!_.isEmpty(noteModalData)}
              onClose={closeNoteModal}
              noteData={{ note }}
              showCheckbox={false}
              onChangeNoteData={changeNote}
              saveNote={saveNote}
              saveLoading={noteSaveLoading}
            />

            <AttentionModal
              onClose={() => setDeletingId('')}
              isOpen={!!deletingId}
              onSave={deleteVisit}
              title="Delete Visit?"
              text="Are you sure you want to delete this visit?"
              loading={deleteLoading}
            />
          </>
        )}
    </div>
  );
};

export default Agenda;
